import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";
import { request } from "@/utils/api";
// import { useSelector, useDispatch } from "react-redux";
import { upsertShipmentsIO } from './shipmentSlice'
// Create entity adapter
const weightDisputeAdapter = createEntityAdapter({
    selectId: (dispute) => dispute._id,
    sortComparer: (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
});

// Async thunk for fetching weight disputes
export const fetchWeightDisputes = createAsyncThunk(
    "weightDispute/fetch",
    async ({ endDate, startDate }, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/user/getWeightDispute",
                method: "GET",
                params: { startDate, endDate }
            });

            if (!response.data || !Array.isArray(response.data)) {
                return rejectWithValue(response.message || "Failed to fetch weight disputes.");
            }

            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error.message || "An unexpected error occurred while fetching weight disputes.");
        }
    }
);

export const takeWeightDisputeAction = createAsyncThunk(
    "weightDispute/takeAction",
    async (ids, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/user/weightDisputeAcceptDispute",
                method: "POST",
                body: { shipmentIds: ids },
                params: { action: 'accept' }
            });

            if (!response.success) {
                return rejectWithValue(response.error.message || "Failed to take action on weight dispute.");
            }

            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error.message || "An unexpected error occurred while taking action.");
        }
    }
);

export const raiseWeightDispute = createAsyncThunk(
    "weightDispute/raiseDispute",
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await request({
                endpoint: "/user/weightDisputeRaiseDispute",
                method: "POST",
                body: data,
                params: {
                    "Content-Type": "multipart/form-data",
                }
            });

            if (!response.success) {
                return rejectWithValue(response.error.message || "Failed to raise weight dispute.");
            }
            dispatch(upsertShipmentsIO(response?.data?.shipments));
            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error.message || "An unexpected error occurred while raising dispute.");
        }
    }
);

// Initial state with adapter
const initialState = weightDisputeAdapter.getInitialState({
    loading: false,
    error: null,
    actionStatus: null,
});

const weightDisputeSlice = createSlice({
    name: "weightDispute",
    initialState,
    reducers: {
        clearWeightDisputes: (state) => {
            weightDisputeAdapter.removeAll(state);
            state.loading = false;
            state.error = null;
            state.actionStatus = null;
        },
        addWeightDisputeIO: (state, action) => {
            weightDisputeAdapter.addOne(state, action.payload);
        },
        updateWeightDisputeIO: (state, action) => {
            weightDisputeAdapter.upsertOne(state, action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchWeightDisputes.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchWeightDisputes.fulfilled, (state, action) => {
                state.loading = false;
                weightDisputeAdapter.setAll(state, action.payload);
            })
            .addCase(fetchWeightDisputes.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(takeWeightDisputeAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.actionStatus = 'pending';
            })
            .addCase(takeWeightDisputeAction.fulfilled, (state, action) => {
                state.loading = false;
                state.actionStatus = 'succeeded';
                console.log("action.payload:::::", action.payload);
                // Handle both object.value and direct array cases
                const payloadArray = action.payload?.shipments || [];

                if (payloadArray.length > 0) {
                    weightDisputeAdapter.upsertMany(state, payloadArray);
                }
            })
            .addCase(takeWeightDisputeAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.actionStatus = 'failed';
            })
            .addCase(raiseWeightDispute.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.actionStatus = 'pending';
            })
            .addCase(raiseWeightDispute.fulfilled, (state, action) => {
                state.loading = false;
                state.actionStatus = 'succeeded';
                // Assuming action.payload is an array of updated disputes
                weightDisputeAdapter.upsertMany(state, action.payload.escalations);
                weightDisputeAdapter.upsertMany(state, action.payload.shipments);


                // updateMany expects an array of update objects with id and changes

            })
            .addCase(raiseWeightDispute.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.actionStatus = 'failed';
            });
    },
});

// Export selectors
export const {
    selectAll: selectAllWeightDisputes,
    selectById: selectWeightDisputeById,
    selectIds: selectWeightDisputeIds,
} = weightDisputeAdapter.getSelectors((state) => state.weightDispute);

// Export actions
export const { clearWeightDisputes, addWeightDisputeIO, updateWeightDisputeIO } = weightDisputeSlice.actions;
export default weightDisputeSlice.reducer;